import { useContext } from "react";
import { useState } from "react";
import { useCallback } from "react";
import { useEffect } from "react";
import { MahiContext } from "../components/state/MahiContext";
import { hikoException } from "../utils/HikoAlert";
import { fetchJSONWithAuth } from "./hikoFetch";


/**
 * 
 * @param {string} URI 
 * @param {object} options to fetch e.g. { method, params, headers, body}
 * @param {boolean} fetchImmediate 
 * @returns {object} E.g. { load, isLoading, data, setData } load is an async function which takes optional (newOptions, newUri)
 * and returns JSON web service results on success, undefined on error (there is also the side effect of showing a hikoAlert on error).
 * Web service results are also stored in data state variable.
 */
export default function useFetchJson(uri, options = {}, fetchImmediate = false, fakeResults = null) {

    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState()
    const [abortController, setAbortController] = useState(new AbortController())

    const theContext = useContext(MahiContext) // this line is changed from Hiko's version of this hook

    const asyncFetchJson = useCallback(async (newOptions = {}, newUri = uri) => {
        const defaultOptions = {
            params: {},
            headers: {},
            method: 'GET',
        }
        const allOptions = {...defaultOptions, ...options, ...newOptions}
        newUri = newUri || uri // If caller pases falsy newUri then use original uri
        setIsLoading(true)
        
        let localAbortController = abortController
        if (abortController.signal.aborted) {
            //
            // this component has been un-mounted previously and the abort controller got aborted.
            // This happens e.g. when ract-scripts reloads the app in development mode.
            // Use a new abort controller.
            //
            localAbortController = new AbortController()
            setAbortController(localAbortController)
        }
        let theData
        try {
            //
            // Are we returning fake data?
            //
            if (fakeResults) {
                theData = fakeResults
            } else {
                theData = await doFetchJson(theContext, newUri, allOptions, localAbortController.signal)
            }
            if (!localAbortController.signal.aborted) { setData(theData) }
        } catch (e) {
            hikoException('Fetch data error', e)
            theData = undefined
            if (!localAbortController.signal.aborted) { setData(theData) }
        } finally {
            // console.log('asyncFetchJson finally localAbortController', localAbortController)
            if (!localAbortController.signal.aborted) { setIsLoading(false) }
        }
        return theData
    // eslint-disable-next-line
    }, []) // depends on abortController, options, hikoContext, uri.

    useEffect(() => {
        if (fetchImmediate) {
            //
            // Fetch data as soon as hook is created.
            //
            asyncFetchJson({}, null) // run in background
        }
        return (() => {
            console.log('useFetchJson useEffect aborting on component unload')
            abortController.abort()
        })
    // eslint-disable-next-line
    }, []) // We need this to fire once at start-up then only execute its return function when the component unmounts.

    // console.log('useFetchJson REFRESH returning abortController.signal:', abortController.signal)
    return { isLoading, data, load: asyncFetchJson, setData }
}





/**
 * 
 * @param {object} theContext 
 * @param {string} bffPath - base path to bff endpoint (E.g. /bff/network-gis-service/api/Job/v1/sendCIMIncrementalPatch)
 * @param {object} options - E.g. { params, headers, method, body }
 * @param {any} abortSignal
 * @returns JSON data on success. Throws exception on error.
 */
const doFetchJson = async (theContext, bffPath, options, abortSignal) => {
    //
    // split queryParams out of options because fetchJSONWithAuth wants them separately 
    //
    const queryParams = options.params
    const fetchOptions = {...options}
    delete fetchOptions.params
    // next line is changed from Hiko's version of this hook. bffHiko -> bffMahi
    const response = await fetchJSONWithAuth(theContext, 'bffMahi', bffPath, queryParams, fetchOptions, abortSignal)
    return response
}
